//authSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  checkUser,
  checkEmail,
  checkMobile,
  createUser,
  fetchStateData,
  fetchIdProofData,
  fetchDistrictData,
  signOut,
  verifyOTP,
  checkMobileDetail,
  checkUserMobile,
  loginWithMobile,
} from "./authAPI";

const initialState = {
  loggedInUser: null,
  token: null,
  status: "idle",
  error: null,
  otp: "",
  mobile: "",
  mobileOtp: "",
  phoneNumber: "",
  isMobileVerified: false,
  stateOptions: [],
  idProofOptions: [],
  districtOptions: [],
};

export const checkEmailAsync = createAsyncThunk(
  "auth/checkEmail",
  async (email) => {
    const response = await checkEmail(email);
    return response;
  }
);

export const checkMobileAsync = createAsyncThunk(
  "auth/checkMobile",
  async (mobile) => {
    const response = await checkMobile(mobile);
    return response;
  }
);

// export const createUserAsync = createAsyncThunk(
//   "user/createUser",
//   async (userData) => {
//     const response = await createUser(userData);
//     return response;
//   }
// );

export const createUserAsync = createAsyncThunk(
  "user/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await createUser(userData);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        statusCode: err.response?.data?.statusCode || 500,
        message: err.response?.data?.message || err.message || "Unknown error",
      });
    }
  }
);

export const checkUserAsync = createAsyncThunk(
  "user/checkUser",
  async (loginInfo, { dispatch }) => {
    const response = await checkUser(loginInfo); // returns { user, token }
    dispatch(setCredentials(response)); // saves to Redux and localStorage
    return response;
  }
);

export const checkUserMobileAsync = createAsyncThunk(
  "user/checkUserMobile",
  async (mobile) => {
    const response = await checkUserMobile(mobile);
    return response;
  }
);

export const loginWithMobileAsync = createAsyncThunk(
  "user/loginWithMobile",
  async ({ mobile, otp }, { dispatch }) => {
    const response = await loginWithMobile({ mobile, otp });
    // dispatch same setCredentials shape as password login
    dispatch(
      setCredentials({
        user: { email: response.email, mobile: response.mobile },
        token: response.token,
      })
    );
    return response;
  }
);

// export const checkMobileDetailAsync = createAsyncThunk(
//   "user/checkMobileDetail",
//   async (mobile) => {
//     const response = await checkMobileDetail(mobile);
//     return response;
//   }
// );

export const checkMobileDetailAsync = createAsyncThunk(
  "user/checkMobileDetail",
  // now take an object rather than just mobile
  async ({ mobile, skipOtp }, { rejectWithValue }) => {
    try {
      // pass skipOtp down into the API call
      const response = await checkMobileDetail(mobile, skipOtp);
      return { ...response, skipOtp };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const verifyOTPAsync = createAsyncThunk(
  "user/verifyOTP",
  async (otp) => {
    const response = await verifyOTP(otp);
    return response;
  }
);

export const fetchStateDataAsync = createAsyncThunk(
  "auth/fetchStateData",
  async () => {
    const response = await fetchStateData();
    return response;
  }
);

export const fetchIdProofDataAsync = createAsyncThunk(
  "auth/fetchIdProofData",
  async () => {
    const response = await fetchIdProofData();
    return response;
  }
);

export const fetchDistrictDataAsync = createAsyncThunk(
  "auth/fetchDistrictData",
  async (stateId) => {
    const response = await fetchDistrictData(stateId);
    return response;
  }
);

export const authSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      state.loggedInUser = action.payload.user;
      state.token = action.payload.token;
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      localStorage.setItem("token", action.payload.token);
    },
    clearCredentials: (state) => {
      state.loggedInUser = null;
      state.token = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },
    setOTP: (state, action) => {
      state.otp = action.payload;
    },
    setPhoneNumber: (state, action) => {
      state.phoneNumber = action.payload;
    },
    setMobileNo: (state, action) => {
      state.mobileNo = action.payload;
    },
    setMobileOTP: (state, action) => {
      state.mobileOtp = action.payload;
    },
    setisMobileVerified: (state, action) => {
      state.isMobileVerified = action.payload;
    },
    resetisMobileVerified: (state) => {
      state.isMobileVerified = false;
      state.mobileOtp = "";
    },
    setDistrictOptions: (state, action) => {
      state.districtOptions = action.payload;
    },
    clearLoggedInUserData: (state) => {
      return initialState;
    },
    setLoginError: (state, action) => {
      state.error = action.payload; // Set error message from the login request
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkEmailAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkEmailAsync.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(checkEmailAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(checkMobileAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkMobileAsync.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(checkMobileAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(createUserAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createUserAsync.fulfilled, (state, action) => {
        state.status = "idle";
        // state.loggedInUser = action.payload;
      })
      .addCase(createUserAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(checkUserAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkUserAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.loggedInUser = action.payload;
      })
      .addCase(checkUserAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(checkUserMobileAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkUserMobileAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.otp = action.payload.otp;
        state.loggedInUser = action.payload;
      })
      .addCase(checkUserMobileAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(checkMobileDetailAsync.pending, (state) => {
        state.status = "loading";
      })
      // .addCase(checkMobileDetailAsync.fulfilled, (state, action) => {
      //   state.status = "idle";
      //   state.mobileOtp = action.payload.mobileOtp;
      //   state.mobileNo = action.payload.mobileNo;

      //   // state.loggedInUser = action.payload;
      // })
      .addCase(checkMobileDetailAsync.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.mobileNo = payload.mobileNo;
        state.email = payload.email;
        state.mobileOtp = payload.skipOtp ? "" : payload.mobileOtp;

        if (payload.skipOtp) {
          // automatically consider them verified
          state.isMobileVerified = true;
        }
      })
      .addCase(checkMobileDetailAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(verifyOTPAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(verifyOTPAsync.fulfilled, (state, action) => {
        console.log("loggedUser =" + action.payload);
        state.status = "idle";
        state.loggedInUser = action.payload;
        state.token = action.payload.token;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        localStorage.setItem("token", action.payload.token);
      })
      .addCase(verifyOTPAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })

      .addCase(fetchStateDataAsync.fulfilled, (state, action) => {
        state.stateOptions = action.payload;
      })
      .addCase(fetchIdProofDataAsync.fulfilled, (state, action) => {
        state.idProofOptions = action.payload;
      })
      .addCase(fetchDistrictDataAsync.fulfilled, (state, action) => {
        state.districtOptions = action.payload;
      });
  },
});

export const {
  setCredentials,
  clearCredentials,
  setOTP,
  setMobileOTP,
  setMobileNo,
  setPhoneNumber,
  setDistrictOptions,
  setisMobileVerified,
  clearLoggedInUserData,
  clearError,
  resetisMobileVerified,
  setLoginError,
} = authSlice.actions;
export const selectStateOptions = (state) => state.auth.stateOptions;
export const selectIdProofOptions = (state) => state.auth.idProofOptions;
export const selectDistrictOptions = (state) => state.auth.districtOptions;
export const selectError = (state) => state.auth.error;
export const selectStatus = (state) => state.auth.status;
export const selectLoggedInUser = (state) => state.auth.loggedInUser;
export const selectAuthToken = (state) => state.auth.token;
export const selectMobileOtp = (state) => state.auth.mobileOtp;
export const selectisMobileVerified = (state) => state.auth.isMobileVerified;

export default authSlice.reducer;
